/**
 * The app shell: sidebar + main (topbar + content).
 *
 * ONE shell for everything that has chrome, public and authenticated alike.
 * It is a single layout route (routes.jsx), so it survives navigation between
 * any two pages inside it — an admin opening Standings keeps the sidebar and
 * its scroll position. Before this the public pages sat in a separate layout
 * branch and the whole shell was torn down on the way in, with no way back.
 *
 * The nav is built from WHO IS LOOKING, not from which branch of the route
 * table they arrived through:
 *
 *   anonymous  Public only
 *   player     Me + Public
 *   admin      Venue + Me + Public
 *
 * Off the shell entirely, and deliberately: Landing, Login, TV, and the
 * scorer/board pages when they arrive. Those own their whole viewport.
 *
 * Mobile is one breakpoint (768px), and the sidebar means two different things
 * either side of it — an overlay drawer below, a column in the flow above. One
 * `navOpen` flag drives both; the effects below are where they differ.
 */

import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAuth, useVenue } from '../context/AuthContext.jsx';
import { apiGet } from '../lib/api.js';
import Icon from './Icon.jsx';

const MOBILE_QUERY = '(max-width: 768px)';

/**
 * Tracks the one breakpoint. Needed in JS, not just CSS, because what the
 * open/close controls DO differs either side of it, and a media query cannot
 * be read from an event handler.
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (event) => setIsMobile(event.matches);

    // Re-read on mount as well as subscribing: the width can have changed
    // between the initial state above and this effect running.
    setIsMobile(mql.matches);
    mql.addEventListener('change', onChange);

    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

/**
 * Venue-scoped nav, filtered at render time.
 *
 *   admin: true  venue_admin and above only
 *   auth:  true  any signed-in user — hidden from anonymous visitors, whose
 *                /my-stats would bounce them to /login
 *   charity: true  only where the venue has charity_enabled set. A venue not
 *                running a charity should not carry a nav entry to a page
 *                about one — see the fetch in AppShell for why this one costs
 *                a request where the venue NAME deliberately does not.
 *
 * Every `to` is relative to /venues/:venueSlug and is prefixed at render.
 */
const VENUE_NAV = [
  {
    label: 'Venue',
    links: [
      { to: '/admin', end: true, icon: 'dashboard', text: 'Dashboard', admin: true },
      { to: '/admin/tournaments',  icon: 'trophy',   text: 'Tournaments', admin: true },
      { to: '/admin/players',      icon: 'users',    text: 'Players',     admin: true },
      { to: '/admin/seasons',      icon: 'calendar', text: 'Seasons',     admin: true },
      { to: '/admin/settings',     icon: 'settings', text: 'Settings',    admin: true },
      { to: '/admin/tv',           icon: 'tv',       text: 'TV settings', admin: true },
      { to: '/admin/charity',      icon: 'heart',    text: 'Charity',     admin: true, charity: true },
      // NO "TV display" LINK HERE. It lives on a tournament's own page now, so
      // that what opens is that tournament rather than whatever the venue
      // happens to have in play — see AdminTournamentDetail. From the nav there
      // was no night to scope it to, which is how an admin ended up casting one
      // tournament and watching another.
    ],
  },
  {
    label: 'Me',
    links: [
      { to: '/my-stats', icon: 'chart', text: 'My stats', auth: true },
    ],
  },
  {
    label: 'Public',
    links: [
      { to: '/standings',    icon: 'chart',  text: 'Standings' },
      { to: '/tournaments',  icon: 'trophy', text: 'Fixtures' },
      { to: '/hall-of-fame', icon: 'target', text: 'Hall of fame' },
    ],
  },
];

/*
 * THERE IS NO ACCOUNT NAV GROUP. Profile is reached by clicking your own name
 * at the foot of the sidebar, which is where a person looks for their own
 * account and which needs no venue in the URL to render.
 *
 * The consequence is deliberate and visible: a signed-in player on /profile or
 * /dashboard has no venue in the URL, so VENUE_NAV does not build and the
 * sidebar carries no groups at all — brand at the top, their name at the
 * bottom. My stats is not a substitute, because it IS per-venue.
 */

const PLATFORM_NAV = [
  {
    label: 'Platform',
    links: [
      { to: '/admin/venues',   icon: 'building', text: 'Venues' },
      { to: '/admin/settings', icon: 'settings', text: 'Platform settings' },
    ],
  },
];

/** venue_admin and above see the admin section. */
const ADMIN_ROLES = new Set(['venue_admin', 'venue_owner']);

export default function AppShell() {
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(() => !window.matchMedia(MOBILE_QUERY).matches);

  const location = useLocation();
  const { venueSlug } = useParams();
  const { user, logout, isAuthenticated } = useAuth();
  const venue = useVenue(venueSlug);

  // Crossing the breakpoint resets to that width's default. Without it,
  // narrowing a window with the sidebar open lands you on a drawer covering
  // the page you were reading.
  useEffect(() => { setNavOpen(!isMobile); }, [isMobile]);

  // Navigation closes the DRAWER, and only the drawer. On mobile it would
  // otherwise sit open over the page just tapped through to. On desktop a
  // collapse is a choice about the session, not about one page, so it has to
  // survive the click — which is why this is not simply unconditional.
  useEffect(() => {
    if (isMobile) setNavOpen(false);
  }, [location.pathname, isMobile]);

  const isPlatformRoute = location.pathname.startsWith('/admin');
  const isPlatformStaff = user?.platformRole === 'platform_owner'
                       || user?.platformRole === 'super_admin';

  // A platform role outranks any venue role, so staff see the admin nav at
  // every venue whether or not they hold a user_venue_roles row there.
  const canAdmin = isPlatformStaff || ADMIN_ROLES.has(venue?.role);

  /**
   * Whether this venue runs a charity, for the one nav entry that depends on
   * it.
   *
   * THIS IS THE EXCEPTION TO THE RULE THE TITLE COMMENT BELOW STATES. Resolving
   * the venue NAME here was rejected because every page already makes that call
   * and the shell would only be duplicating it; nothing else fetches
   * charity_enabled, so there is no call to duplicate. It is one public GET,
   * only for admins, only where there is a venue in the URL — and the
   * alternative is a permanent nav entry to a page that exists to say the
   * feature is switched off.
   *
   * Absent or failed reads leave it false, which hides the link. A venue that
   * does run a charity and briefly cannot say so loses a nav entry for a
   * moment; the route still works for anyone who has it bookmarked.
   */
  const [charityOn, setCharityOn] = useState(false);

  useEffect(() => {
    if (!venueSlug || !canAdmin) { setCharityOn(false); return undefined; }

    let cancelled = false;

    (async () => {
      const { data } = await apiGet(`/api/venues/by-slug/${venueSlug}`);
      if (cancelled || !data?.venue) return;

      const pub = await apiGet(`/api/venues/${data.venue.id}/settings/public`);
      if (!cancelled) setCharityOn(pub.data?.settings?.charity_enabled === '1');
    })();

    return () => { cancelled = true; };
  }, [venueSlug, canAdmin]);


  // null, not '', when the URL carries no venue. /dashboard, /register-venue
  // and the 404 all render in this shell without one, and prefixing with an
  // empty string produced /standings — not a route, so every venue link in the
  // sidebar quietly led to the 404 page.
  const base = venueSlug ? `/venues/${venueSlug}` : null;

  let groups = [];

  if (isPlatformRoute) {
    groups = PLATFORM_NAV;
  } else {
    // The venue groups need a venue; the account group never does.
    groups = (base ? VENUE_NAV : [])
      .map((group) => ({
        ...group,
        links: group.links.filter(
          (link) => (!link.admin || canAdmin)
                 && (!link.auth || isAuthenticated)
                 && (!link.charity || charityOn)
        ),
      }))
      .filter((group) => group.links.length > 0);
  }

  const title = isPlatformRoute
    ? 'Platform administration'
    // An anonymous visitor has no venues list to read the name out of, so a
    // public page shows the wordmark rather than the venue. Resolving it would
    // mean a by-slug call here duplicating the one every page already makes.
    : (venue?.name ?? 'HOPDARTS');

  return (
    <div className={`hd-shell${navOpen ? ' hd-nav-open' : ''}`}>
      <aside className="hd-sidebar">
        <div className="hd-sidebar-head">
          <NavLink to={isAuthenticated ? '/dashboard' : '/'} className="hd-brand">
            HOPDARTS
          </NavLink>

          <button
            type="button"
            className="hd-sidebar-close"
            aria-label="Hide navigation"
            onClick={() => setNavOpen(false)}
          >
            <Icon name="chevron-left" size={18} className="" />
          </button>
        </div>

        {groups.map((group) => (
          <nav className="hd-nav-group" key={group.label}>
            <div className="hd-nav-group-label">{group.label}</div>
            {group.links.map((link) => {
              // Platform links are absolute already; the venue groups are
              // relative to /venues/:venueSlug.
              const to = isPlatformRoute ? link.to : `${base}${link.to}`;

              return (
                <NavLink
                  key={link.to}
                  to={to}
                  end={link.end}
                  className={({ isActive }) =>
                    `hd-nav-link${isActive ? ' is-active' : ''}`}
                >
                  <Icon name={link.icon} />
                  {link.text}
                </NavLink>
              );
            })}
          </nav>
        ))}

        {isPlatformStaff && !isPlatformRoute && (
          <nav className="hd-nav-group">
            <div className="hd-nav-group-label">Platform</div>
            <NavLink to="/admin/venues" className="hd-nav-link">
              <Icon name="building" />
              All venues
            </NavLink>
          </nav>
        )}

        {/* The way to /profile. A name is what a person looks for when they
            want their own account, and unlike a nav group it needs no venue in
            the URL. Only a CUSTOM avatar is drawn — falling back to the default
            image here would put a grey circle beside every player who has never
            uploaded one. */}
        {isAuthenticated && user?.name && (
          <Link to="/profile" className="hd-sidebar-user">
            {user.profilePicUrl && (
              <img src={user.profilePicUrl} alt="" className="hd-sidebar-avatar" />
            )}
            <span>{user.name}</span>
          </Link>
        )}
      </aside>

      {/* Mobile only: on desktop the sidebar is in the flow and there is
          nothing to dismiss. */}
      {navOpen && isMobile && (
        <button
          type="button"
          className="hd-scrim"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
        />
      )}

      <div className="hd-main">
        <header className="hd-topbar">
          {!navOpen && (
            <button
              type="button"
              className="hd-hamburger"
              aria-label="Show navigation"
              aria-expanded={false}
              onClick={() => setNavOpen(true)}
            >
              <Icon name={isMobile ? 'menu' : 'chevron-right'} size={18} className="" />
            </button>
          )}

          <div className="hd-topbar-title">{title}</div>
          <div className="hd-topbar-spacer" />

          {isAuthenticated ? (
            <>
              <div className="hd-topbar-user">{user?.email}</div>
              <button type="button" className="hd-btn hd-btn-quiet hd-btn-sm" onClick={logout}>
                Sign out
              </button>
            </>
          ) : (
            <>
              {/* Primary, and first: signing up is the thing a new visitor is
                  here to do, and until now the only way in was the contextual
                  prompt on a tournament they had to find first. */}
              <Link to="/signup" className="hd-btn hd-btn-primary hd-btn-sm">
                Sign up
              </Link>
              <Link to="/login" className="hd-btn hd-btn-quiet hd-btn-sm">
                Sign in
              </Link>
            </>
          )}
        </header>

        <main className="hd-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
